import { Link, useParams } from "react-router-dom";
import LegalMarkdown from "../components/LegalMarkdown";
import CookiePreferencesTrigger from "../components/CookiePreferencesTrigger";
import { hasOptionalTrackingServices } from "../lib/cookieConsent";
import { getLegalDoc, getLegalVersion } from "../legal/legalDocuments";
import { SITE_NAME, buildPublicPageUrl } from "../lib/pageSeo";
import Nd1LegalShell from "../nd1/components/Nd1LegalShell";
import "../nd1/nd1.css";

const SEO_DESCRIPTIONS = {
  terms:
    "Read the JOBUWIN Terms of Use — the agreement that keeps hiring genuine, honest and human for everyone on the platform.",
  privacy:
    "Read the JOBUWIN Privacy Policy — how JOBUWIN collects, uses, stores and protects your personal information.",
  cookies:
    "Read the JOBUWIN Cookie Policy — how we use cookies and similar technologies on www.jobuwin.com.",
};

const PUBLISHER = {
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "MotoMob Limited",
  url: buildPublicPageUrl("/"),
};

function buildLegalJsonLd({ doc, resolved, description }) {
  if (!doc || !resolved) return undefined;
  const canonical = buildPublicPageUrl(doc.path);
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `${doc.label} | ${SITE_NAME}`,
      description,
      url: canonical,
      inLanguage: "en-GB",
      ...(resolved.effectiveDateIso
        ? { datePublished: resolved.effectiveDateIso, dateModified: resolved.effectiveDateIso }
        : {}),
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: buildPublicPageUrl("/") },
      publisher: PUBLISHER,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: buildPublicPageUrl("/") },
        { "@type": "ListItem", position: 2, name: doc.label, item: canonical },
      ],
    },
  ];
}

export default function LegalDocumentPage({ docType }) {
  const { version } = useParams();
  const doc = getLegalDoc(docType);
  const resolved = getLegalVersion(docType, version);
  const description = SEO_DESCRIPTIONS[docType] || "JOBUWIN legal information.";
  const isCurrent = Boolean(resolved && resolved.isCurrent);
  const seoTitle = doc
    ? isCurrent || !resolved
      ? doc.label
      : `${doc.label} (v${resolved.version})`
    : "Legal";
  const pagePath =
    resolved && !isCurrent && doc
      ? `${doc.path}/${resolved.version}`
      : doc
        ? doc.path
        : "/";

  const legalArticle = resolved ? (
    <article className="nd1-card nd1-legal__card">
      <header className="nd1-legal__head">
        <span className="nd1-auth-card__eyebrow">{doc.label}</span>
        <p className="nd1-legal__meta">
          Version {resolved.version} &middot; Effective {resolved.effectiveDate}
        </p>
        {!resolved.isCurrent ? (
          <p className="nd1-legal__archived">
            You&apos;re viewing an older version.{" "}
            <Link to={doc.path}>Read the current {doc.label}</Link>.
          </p>
        ) : null}
      </header>
      <LegalMarkdown markdown={resolved.content} />
      {docType === "cookies" && hasOptionalTrackingServices() ? (
        <div className="nd1-legal__cookie-settings">
          <p>Want to change what we load? Update your cookie preferences any time.</p>
          <CookiePreferencesTrigger className="jw-public-button jw-public-button--outline" />
        </div>
      ) : null}
    </article>
  ) : (
    <article className="nd1-card nd1-legal__card">
      <h1>We couldn&apos;t find that document</h1>
      <p>
        That version isn&apos;t available.{" "}
        {doc ? (
          <Link to={doc.path}>Read the current {doc.label}</Link>
        ) : (
          <Link to="/">Head back home</Link>
        )}
        .
      </p>
    </article>
  );

  return (
    <Nd1LegalShell
      seo={{
        title: seoTitle,
        description,
        path: pagePath,
        canonicalPath: doc ? doc.path : pagePath,
        type: "article",
        articlePublishedTime: resolved?.effectiveDateIso,
        articleModifiedTime: resolved?.effectiveDateIso,
        noindex: !resolved || !isCurrent,
        jsonLd: isCurrent
          ? buildLegalJsonLd({ doc, resolved, description })
          : undefined,
      }}
      backTo="/"
      breadcrumbLabel={doc?.label}
      breadcrumbPath={doc?.path}
    >
      {legalArticle}
    </Nd1LegalShell>
  );
}
